import {
  BrainCircuitIcon,
  ContentSwitcher,
  IconButton,
  SingleSelectDropdown,
} from "@carbon6/c6-frontend-design";
import { useCallback, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { useAppState } from "@/app";
import { hatcheryDropdownModes } from "@/app/theme";
import { GnbMenu } from "@/components/base";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
import {
  readShareLinkScope,
  writeShareLinkScope,
  type ShareLinkScope,
} from "./shareLinks/shareLinkScope";
import { ShareLinksPanel } from "./shareLinks/ShareLinksPanel";
import { SubmitSessionsPanel } from "./SubmitSessionsPanel";
import { TrainingNotificationToggle } from "./TrainingNotificationToggle";
import { openTrainingSubmitWindow } from "./trainingSubmitWindow";
import { useTeamGroupSelection } from "./useTeamGroupSelection";
import {
  clearPolicyFilterParams,
  scopeSearchParamKey,
  writeScopeParam,
} from "./policyFilterParams";
import type { SessionScope } from "@/modules/training/public";
import {
  defaultTrainingTab,
  normalizeTrainingTab,
  shareLinksTabValue,
  trainingTabItems,
  trainingTabSearchParamKey,
} from "./trainingTabs";

// Training 은 학습 세션 목록 단일 화면이다. Policy 카탈로그는 Evaluation 페이지가 소유한다. 내
// 목록과 팀 목록은 화면 구성이 같고 스코프만 다르다. 그래서 Evaluation 과 같은 Content Switcher
// 로 구분한다(`?scope=`). 데이터셋 목록은 공용 Dataset 페이지가 소유한다.
//
// 스코프 스위처는 제목 바로 옆(titleAside)에 둔다. 스코프는 표를 좁히는 조건이 아니라 어느
// 목록인지를 정한다. 그래서 필터 줄이 아니라 제목 옆에 둔다. 팀 그룹 선택은 스코프에 딸린
// 축이라 필터 줄 앞자리에 둔다. 두 상태 모두 페이지가 소유하고 컨트롤 노드만 전달한다. 새 학습
// 진입점은 스코프와 독립인 페이지급 액션이라 제목 우측 끝(headerActions)에 둔다. 데이터셋 업로드
// 진입점은 사이드바와 Dataset 페이지가 소유하므로 이 페이지에는 없다.
//
// 공유 링크 탭(`?tab=shares`)은 발급한 링크의 발행 기록이다. 세션 스코프(My/Team)와 그룹은
// 세션 목록의 축이라 그 탭에서는 숨긴다. URL 키는 유지하므로 돌아오면 복원된다. admin 에게만 같은
// 자리에 링크 범위(내 링크·전체) 스위처를 표시한다.

const scopeOptions = [
  { value: "me", label: "My" },
  { value: "team", label: "Team" },
];

const shareScopeOptions = [
  { value: "me", label: "내 링크" },
  { value: "all", label: "전체" },
];

function normalizeScope(value: string | null): SessionScope {
  return value === "team" ? "team" : "me";
}

export function TrainingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = normalizeScope(searchParams.get(scopeSearchParamKey));

  // 그룹 목록은 team 스코프에서만 필요하다.
  const group = useTeamGroupSelection(scope === "team", clearPolicyFilterParams);

  // 스코프 전환은 writeScopeParam 으로 커밋한다. 오래된 딥링크로 남은 member/experiment 필터
  // 키도 같은 커밋에서 지운다. 그래서 정리된 URL 이 canonical 이 된다.
  const handleScopeChange = useCallback(
    (next: string | null) => setSearchParams((prev) => writeScopeParam(prev, next)),
    [setSearchParams],
  );

  const { state: appState } = useAppState();
  const isAdmin = appState.currentUser?.role === "admin";
  const tab = normalizeTrainingTab(searchParams.get(trainingTabSearchParamKey));
  const shareScope = readShareLinkScope(searchParams, isAdmin);

  // 기본 탭이면 키를 지운다. 그래서 정리된 URL 이 canonical 이 된다. 다른 키(스코프·그룹·필터)는
  // 두 탭을 오가도 남는다.
  const handleTabChange = useCallback(
    (next: string) =>
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (next === defaultTrainingTab) {
          params.delete(trainingTabSearchParamKey);
        } else {
          params.set(trainingTabSearchParamKey, next);
        }
        return params;
      }),
    [setSearchParams],
  );
  const handleShareScopeChange = useCallback(
    (next: string | null) =>
      setSearchParams((prev) => writeShareLinkScope(prev, next === "all" ? "all" : "me"), {
        replace: true,
      }),
    [setSearchParams],
  );

  // 새 학습은 제출 위저드 Window 를 연다. 제출은 actor 스코프라 필터 줄의 스코프 선택과 독립이다.
  // 제출 뒤 상세 열기는 위저드가 완료 toast 의 [열기] 로 제안한다. 진입점마다 다르게 동작하지
  // 않는다.
  const handleNewTraining = useCallback(() => {
    openTrainingSubmitWindow();
  }, []);

  // 제목 우측 헤더 액션. 알림 대상이 내가 제출한 학습이므로 알림 토글을 제출 버튼 옆에 둔다.
  // 목록을 좁히는 축이 아니라 페이지급 설정이라 필터 줄이 아니라 헤더에 둔다.
  const headerActions = (
    <>
      <TrainingNotificationToggle />
      <IconButton text="새 Training" variant="solid" onClick={handleNewTraining}>
        <BrainCircuitIcon />
      </IconButton>
    </>
  );

  // 제목 옆 스코프 컨트롤. size 를 지정하지 않아 c6 기본(md)을 쓴다. 제목 줄의 헤더 액션과 같은 높이다.
  const scopeSwitcher = (
    <ContentSwitcher
      label="스코프"
      value={scope}
      options={scopeOptions}
      onValueChange={handleScopeChange}
    />
  );

  // 공유 링크 탭의 제목 옆. admin 만 조직 전체를 고른다. 다른 등급은 내 링크만 있으므로 표시하지 않는다.
  const shareScopeSwitcher = isAdmin ? (
    <ContentSwitcher
      label="링크 범위"
      value={shareScope}
      options={shareScopeOptions}
      onValueChange={handleShareScopeChange}
    />
  ) : undefined;

  // 팀 스코프에서만 그룹을 고른다. me 스코프에는 좁힐 팀이 없다.
  const groupFilter =
    scope === "team" ? (
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="그룹"
        value={group.selectedGroupUuid ?? ""}
        options={group.options}
        onValueChange={group.onGroupChange}
        placeholder="선택할 그룹이 없습니다."
        disabled={!group.hasGroups}
      />
    ) : null;

  return (
    <TrainingPageView
      pageTitle="Training"
      currentMenu={GnbMenu.Training}
      scope={scope}
      groupUuid={scope === "team" ? group.selectedGroupUuid : null}
      groupFilter={groupFilter}
      titleAside={tab === shareLinksTabValue ? shareScopeSwitcher : scopeSwitcher}
      headerActions={headerActions}
      shareScope={shareScope}
      tabs={{
        items: trainingTabItems,
        value: tab,
        onValueChange: handleTabChange,
        ariaLabel: "Training 탭",
      }}
    />
  );
}

type TrainingPageViewProps = PageBaseProps & {
  scope: SessionScope;
  // 팀 스코프 전용. me 스코프이거나 소속 팀이 없으면 null 이다.
  groupUuid: string | null;
  // 팀 스코프에서 필터 줄 앞자리에 넣을 그룹 드롭다운. me 스코프면 null 이다.
  groupFilter: ReactNode;
  shareScope: ShareLinkScope;
};

class TrainingPageView extends PageBase<TrainingPageViewProps> {
  // 테이블 source 가 생성자에서 scope 를 확정한다. 그래서 key 로 스코프마다 다시 마운트한다.
  // key 가 없으면 Team 으로 바꿔도 source 가 me 엔드포인트를 계속 호출한다.
  protected override renderContent(context: PageBaseContext): ReactNode {
    const { scope, groupUuid, groupFilter, shareScope } = this.props;

    // 범위마다 source 를 새로 만들지 않는다. 패널이 setScope 로 1페이지부터 다시 받는다.
    if (context.currentTab === shareLinksTabValue) {
      return <ShareLinksPanel scope={shareScope} />;
    }

    if (scope === "team") {
      return (
        <SubmitSessionsPanel
          key={scope}
          scope="team"
          groupUuid={groupUuid}
          groupFilter={groupFilter}
        />
      );
    }
    return <SubmitSessionsPanel key={scope} scope="me" />;
  }
}
